import { FormEvent, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { restablecerPassword } from "@/lib/api";
import { extractApiError } from "@/lib/utils";

const LONGITUD_MINIMA = 10;

/**
 * Página pública que consume el token del enlace del correo.
 * Ruta: /restablecer?token=XXXX
 */
export function RestablecerPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") ?? "";

  const [nueva, setNueva] = useState("");
  const [repetida, setRepetida] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState(false);
  const [enviando, setEnviando] = useState(false);

  const coinciden = nueva.length > 0 && nueva === repetida;
  const listo = token && coinciden && nueva.length >= LONGITUD_MINIMA && !enviando;

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setEnviando(true);
    try {
      await restablecerPassword(token, nueva);
      setOk(true);
      setTimeout(() => navigate("/login", { replace: true }), 2500);
    } catch (err) {
      setError(extractApiError(err, "No se pudo restablecer la contraseña."));
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div
      className="min-h-screen grid place-items-center"
      style={{ background: "var(--bg-2)" }}
    >
      <div className="w-full max-w-md p-10 stack stack-5">
        <div className="text-center stack stack-3">
          <div className="brand-mark mx-auto" style={{ width: 44, height: 44, fontSize: 16 }}>
            G
          </div>
          <div>
            <h1
              style={{
                fontFamily: "var(--font-serif)",
                fontSize: 28,
                fontWeight: 500,
                letterSpacing: "-0.02em",
                margin: 0,
              }}
            >
              Restablecer contraseña
            </h1>
            <p style={{ color: "var(--ink-3)", fontSize: 14, margin: 0 }}>
              Elige una contraseña nueva para tu cuenta.
            </p>
          </div>
        </div>

        {!token ? (
          <div className="banner banner--red">
            <span className="banner__icon">⚠</span>
            <div style={{ fontSize: 13 }}>
              Falta el token del enlace. Abre el enlace completo desde el
              correo, o <Link to="/login">solicita uno nuevo</Link>.
            </div>
          </div>
        ) : ok ? (
          <div className="banner banner--green">
            <span className="banner__icon">✓</span>
            <div style={{ fontSize: 13 }}>
              Contraseña restablecida. Te llevamos al inicio de sesión…
            </div>
          </div>
        ) : (
          <form onSubmit={enviar} className="stack stack-4">
            <div className="field">
              <label htmlFor="nueva">Nueva contraseña</label>
              <input
                id="nueva"
                type="password"
                required
                autoFocus
                value={nueva}
                onChange={(e) => setNueva(e.target.value)}
                className="input"
                autoComplete="new-password"
              />
              <span className="hint">
                Mínimo {LONGITUD_MINIMA} caracteres. Una frase larga es más
                segura y fácil de recordar.
              </span>
            </div>

            <div className="field">
              <label htmlFor="repetida">Repite la nueva contraseña</label>
              <input
                id="repetida"
                type="password"
                required
                value={repetida}
                onChange={(e) => setRepetida(e.target.value)}
                className="input"
                autoComplete="new-password"
              />
              {repetida.length > 0 && !coinciden && (
                <span className="hint" style={{ color: "var(--red)" }}>
                  Las contraseñas no coinciden.
                </span>
              )}
            </div>

            {error && (
              <div className="banner banner--red">
                <span className="banner__icon">⚠</span>
                <div>{error}</div>
              </div>
            )}

            <button
              type="submit"
              className="btn btn--primary btn--block btn--lg"
              disabled={!listo}
            >
              {enviando ? "Guardando…" : "Restablecer contraseña"}
            </button>
          </form>
        )}

        <p className="muted text-center" style={{ fontSize: 12, marginTop: 0 }}>
          <Link to="/login">Volver al inicio de sesión</Link>
        </p>
      </div>
    </div>
  );
}
